import React from 'react';
import useToggle from '../player-controls/_hooks/use-toggle';

const AUTO_HIDE_TIMEOUT = 3000;

export interface PlayerControls {
  handlePlayerMouseMove: () => void;
  handlePlayerMouseLeave: () => void;
  handlePlayerMouseEnter: () => void;
  disablePlayerMouseMove: () => void;
  enablePlayerMouseMove: () => void;
  fullscreen: boolean;
  closeControls: () => void;
  openControls: () => void;
  handlePlayerControlsMouseEnter: () => void;
  handlePlayerControlsMouseLeave: () => void;
  controlsOpened: boolean;
  handleToggleFullscreen: () => void;
}

interface PlayerControlsProps {
  children: React.ReactNode | React.ReactNode[];
}

export const PlayerControlsContext = React.createContext<PlayerControls>({} as PlayerControls);

export const PlayerControlsConsumer = PlayerControlsContext.Consumer;

export const PlayerControlsProvider = (props: PlayerControlsProps): any => {
  const { children } = props;

  const autoHideTimeout = React.useRef<number | null>(null);

  const [opened, setOpened] = React.useState(false);

  const [hovered, setHovered] = React.useState(false);

  const [fullscreen, toggleFullscreen] = useToggle(false);

  const [playerMouseMoveDisabled, setPlayerMouseMoveDisabled] = React.useState(false);

  const handleClearAutoHideTimeout = React.useCallback(() => {
    if (autoHideTimeout.current) {
      clearTimeout(autoHideTimeout.current);
      autoHideTimeout.current = null;
    }
  }, []);

  const closeControls = React.useCallback(() => {
    setOpened(false);

    handleClearAutoHideTimeout();
  }, [handleClearAutoHideTimeout]);

  const handleSetAutoHideTimeout = React.useCallback(() => {
    autoHideTimeout.current = window.setTimeout(() => {
      if (!hovered) {
        closeControls();
      }
    }, AUTO_HIDE_TIMEOUT);
  }, [closeControls, hovered]);

  const openControls = React.useCallback(() => {
    setOpened(true);

    handleClearAutoHideTimeout();

    handleSetAutoHideTimeout();
  }, [handleClearAutoHideTimeout, handleSetAutoHideTimeout]);

  const disablePlayerMouseMove = React.useCallback(() => {
    setPlayerMouseMoveDisabled(true);
  }, []);

  const enablePlayerMouseMove = React.useCallback(() => {
    setPlayerMouseMoveDisabled(false);
  }, []);

  const handlePlayerMouseEnter = React.useCallback(() => {
    openControls();
  }, [openControls]);

  const handlePlayerMouseMove = React.useCallback(() => {
    if (playerMouseMoveDisabled) return;

    openControls();
  }, [openControls, playerMouseMoveDisabled]);

  const handlePlayerMouseLeave = React.useCallback(() => {
    closeControls();
  }, [closeControls]);

  const handlePlayerControlsMouseEnter = React.useCallback(() => {
    setHovered(true);
  }, []);

  const handlePlayerControlsMouseLeave = React.useCallback(() => {
    setHovered(false);
  }, []);

  const handleToggleFullscreen = React.useCallback(() => {
    toggleFullscreen();
  }, [toggleFullscreen]);

  return (
    <PlayerControlsContext.Provider
      value={{
        controlsOpened: opened,
        handlePlayerMouseMove,
        handlePlayerMouseLeave,
        handlePlayerMouseEnter,
        closeControls,
        openControls,
        disablePlayerMouseMove,
        enablePlayerMouseMove,
        fullscreen,
        handleToggleFullscreen,
        handlePlayerControlsMouseEnter,
        handlePlayerControlsMouseLeave
      }}
    >
      {children}
    </PlayerControlsContext.Provider>
  );
};

export default PlayerControlsProvider;
